// apps/admin/src/features/billing/TransferGroupForm.tsx
//
// Phase 8.2 Wave 3 Task 21 — extracted Create + Edit form modal for
// Transfer Groups, reused by TransferGroupsPage.tsx.
//
// Per CONTEXT.md / API-CONTRACTS.md §Finance — Transfer Groups:
//   - Transfer Group whitelist + per-member limits.
//   - Per-member transfer-block flag (block surface lives in 08.1;
//                 this form does not toggle block — only members dialog renders
//                 the read-only flag derived from MerchantTransferBlock).
//
// Endpoints:
//   - POST  /api/v1/admin/transfer-groups
//   - PATCH /api/v1/admin/transfer-groups/:id
//
// Contract behaviour:
//   - Name required, 1-80 chars, unique → 409 TRANSFER_GROUP_NAME_TAKEN inline.
//   - Min/max amount optional integers (cents); min ≤ max validated client-side.
//   - Idempotency-Key on both POST + PATCH (mutating verb policy).
//   - Edit mode hydrates the form from `initialGroup`; Create mode starts blank.
//
// Test IDs (from PLAN/task-21.md <test_ids>):
//   - transfer-group-create-btn       — owned by parent page
//   - transfer-group-edit-btn-{id}    — owned by parent page
//   - transfer-group-form             — modal root (this file)
//   - transfer-group-name             — name input
//   - transfer-group-submit-btn       — submit button
//
// vg-binding: PLAN:task-21
// vg-binding: P8.D-89
// vg-binding: P8.D-458
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:idempotency-key

import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Wire types — match TransferGroupsPage.tsx serializer shape (legacy P3
// contract still in production; 08.2 contract Block 2 is BE-additive).
// ---------------------------------------------------------------------------

export interface TransferGroupFormGroup {
  id: string;
  name: string;
  description: string | null;
  min_amount: number | null;
  max_amount: number | null;
}

export interface TransferGroupFormState {
  mode: 'create' | 'edit';
  group?: TransferGroupFormGroup;
}

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

interface ApiError {
  code?: string;
  message: string;
  status: number;
}

function envelopeError(payload: unknown, status: number, t: (key: string) => string): ApiError {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    const err = (payload as ErrorEnvelope).error;
    return { code: err?.code, message: err?.message ?? t('transfer_group.toast.unknownError'), status };
  }
  return { message: t('transfer_group.toast.networkErrorDesc'), status };
}

// Idempotency-Key helper for mutating endpoints (mutating verb policy).
function idemKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `tg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface TransferGroupFormProps {
  state: TransferGroupFormState | null;
  onClose: () => void;
  onSaved: () => void;
}

export function TransferGroupForm({
  state,
  onClose,
  onSaved,
}: TransferGroupFormProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);

  useEffect(() => {
    if (!state) return;
    if (state.mode === 'edit' && state.group) {
      setName(state.group.name);
      setDescription(state.group.description ?? '');
      // Stored as integer cents; the form edits in USD (major units).
      setMinAmount(state.group.min_amount != null ? (state.group.min_amount / 100).toString() : '');
      setMaxAmount(state.group.max_amount != null ? (state.group.max_amount / 100).toString() : '');
    } else {
      setName('');
      setDescription('');
      setMinAmount('');
      setMaxAmount('');
    }
    setNameError(null);
    setGeneralError(null);
    setSubmitting(false);
  }, [state]);

  if (!state) return null;

  async function handleSubmit(): Promise<void> {
    if (!state) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t('transfer_group.form.nameRequired'));
      return;
    }
    if (trimmed.length > 80) {
      setNameError(t('transfer_group.form.nameTooLong'));
      return;
    }
    // Amounts are entered in USD (major units); the BE contract stores integer
    // cents, so we validate the dollar input then convert on submit.
    const minVal = minAmount ? Number(minAmount) : undefined;
    const maxVal = maxAmount ? Number(maxAmount) : undefined;
    if (minVal !== undefined && Number.isNaN(minVal)) {
      setGeneralError(t('transfer_group.form.minNumber'));
      return;
    }
    if (maxVal !== undefined && Number.isNaN(maxVal)) {
      setGeneralError(t('transfer_group.form.maxNumber'));
      return;
    }
    // At most 2 decimal places (cent precision) — never silently round input.
    const tooManyDecimals = (s: string): boolean => /\.\d{3,}$/.test(s.trim());
    if ((minAmount && tooManyDecimals(minAmount)) || (maxAmount && tooManyDecimals(maxAmount))) {
      setGeneralError(t('transfer_group.form.amountDecimals'));
      return;
    }
    if ((minVal !== undefined && minVal < 0) || (maxVal !== undefined && maxVal < 0)) {
      setGeneralError(t('transfer_group.form.amountNegative'));
      return;
    }
    if (minVal !== undefined && maxVal !== undefined && minVal > maxVal) {
      setGeneralError(t('transfer_group.form.minMax'));
      return;
    }
    const minCents = minVal !== undefined ? Math.round(minVal * 100) : undefined;
    const maxCents = maxVal !== undefined ? Math.round(maxVal * 100) : undefined;

    setNameError(null);
    setGeneralError(null);
    setSubmitting(true);
    try {
      const client = apiClient();
      const body: Record<string, unknown> = {
        name: trimmed,
        description: description.trim() || undefined,
        min_amount: minCents,
        max_amount: maxCents,
      };
      const isEdit = state.mode === 'edit' && state.group;
      const path = isEdit
        ? `/api/v1/admin/transfer-groups/${encodeURIComponent(state.group!.id)}`
        : '/api/v1/admin/transfer-groups';
      const headers: Record<string, string> = { 'Idempotency-Key': idemKey() };
      const method = isEdit ? 'PATCH' : 'POST';
      const verbClient = client as unknown as Record<
        'POST' | 'PATCH',
        (
          p: string,
          init: { body: unknown; headers?: Record<string, string> },
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      >;
      const { error, response } = await verbClient[method](path, {
        body,
        headers,
      });
      if (error || !response.ok) {
        const env = envelopeError(error, response.status, t);
        // 409 TRANSFER_GROUP_NAME_TAKEN → inline name field error.
        if (env.code === 'TRANSFER_GROUP_NAME_TAKEN' || response.status === 409) {
          setNameError(env.message || t('transfer_group.form.nameTaken'));
          return;
        }
        setGeneralError(env.message);
        toast({
          title: isEdit
            ? t('transfer_group.form.updateFailed')
            : t('transfer_group.form.createFailed'),
          description: env.message,
          variant: 'error',
        });
        return;
      }
      toast({
        title: isEdit
          ? t('transfer_group.form.updated')
          : t('transfer_group.form.created'),
        description: trimmed,
        variant: 'success',
      });
      onClose();
      onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('transfer_group.toast.checkConnection');
      setGeneralError(msg);
      toast({ title: t('transfer_group.toast.networkError'), description: msg, variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <div data-testid="transfer-group-form">
        <DialogHeader>
          <DialogTitle>
            {state.mode === 'edit'
              ? t('transfer_group.form.editTitle')
              : t('transfer_group.form.createTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="tg-name" className="mb-1 block text-sm font-medium text-pw-text">
              {t('transfer_group.form.nameLabel')} <span className="text-rose-600 dark:text-rose-300">*</span>
            </label>
            <Input
              id="tg-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              placeholder={t('transfer_group.form.namePlaceholder')}
              aria-invalid={nameError ? 'true' : 'false'}
              data-testid="transfer-group-name"
            />
            {nameError ? (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert" data-testid="tg-name-error">
                {nameError}
              </p>
            ) : (
              <p className="mt-1 text-xs text-pw-text-muted">
                {t('transfer_group.form.nameHelp')}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="tg-description" className="mb-1 block text-sm font-medium text-pw-text">
              {t('transfer_group.columns.description')}
            </label>
            <textarea
              id="tg-description"
              className="block h-20 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              placeholder={t('transfer_group.form.descriptionPlaceholder')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="tg-min-amount" className="mb-1 block text-sm font-medium text-pw-text">
                {t('transfer_group.form.minAmount')}
              </label>
              <Input
                id="tg-min-amount"
                type="number"
                inputMode="decimal"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                placeholder="0.00"
                min={0}
                step="0.01"
              />
            </div>
            <div>
              <label htmlFor="tg-max-amount" className="mb-1 block text-sm font-medium text-pw-text">
                {t('transfer_group.form.maxAmount')}
              </label>
              <Input
                id="tg-max-amount"
                type="number"
                inputMode="decimal"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                placeholder="1000.00"
                min={0}
                step="0.01"
              />
            </div>
          </div>
          {generalError ? (
            <p className="rounded-md bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-xs text-rose-700 dark:text-rose-300" role="alert">
              {generalError}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('transfer_group.form.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="transfer-group-submit-btn"
          >
            {submitting
              ? state.mode === 'edit'
                ? t('transfer_group.form.saving')
                : t('transfer_group.form.creating')
              : state.mode === 'edit'
                ? t('transfer_group.form.saveChanges')
                : t('transfer_group.form.createGroup')}
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}

export default TransferGroupForm;
